/**
 * AssignmentRecitationRecorder — record + preview + upload audio for a
 * recitation-type assignment. Mobile-first; large mic button.
 *
 * Browser API: MediaRecorder. We record to webm/opus on Chrome/Android
 * and to mp4/aac on Safari/iOS, then base64-encode for upload.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, Square, Loader2, RefreshCcw, Send, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { GlassCard } from '../shared';
import { acquireMicrophoneStream, pickRecorderMime, MicError } from '../../services/microphoneService';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => {
  const t = localStorage.getItem('noor_albayan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const AssignmentRecitationRecorder = ({ assignmentId, onSubmitted }) => {
  const [phase, setPhase] = useState('idle'); // idle | recording | reviewing | uploading | done
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mimeType, setMimeType] = useState('audio/webm');

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => () => {
    try { recorderRef.current?.state === 'recording' && recorderRef.current.stop(); } catch {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (tickRef.current) clearInterval(tickRef.current);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    setError('');
    let stream;
    try {
      stream = await acquireMicrophoneStream();
    } catch (err) {
      if (err instanceof MicError) {
        setError(err.message);
      } else {
        setError("Impossible d'accéder au microphone. Vérifie les autorisations.");
      }
      // eslint-disable-next-line no-console
      console.error('mic acquire failed', err);
      return;
    }

    try {
      streamRef.current = stream;
      const mt = pickRecorderMime();
      setMimeType(mt || 'audio/webm');
      const rec = mt
        ? new MediaRecorder(stream, { mimeType: mt, audioBitsPerSecond: 64000 })
        : new MediaRecorder(stream, { audioBitsPerSecond: 64000 });
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onerror = (e) => {
        // eslint-disable-next-line no-console
        console.error('MediaRecorder error', e);
        setError("Erreur d'enregistrement. Vérifie le microphone et réessaie.");
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mt || 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setPhase('reviewing');
        streamRef.current?.getTracks().forEach((t) => t.stop());
      };
      // Slice into 1-second chunks for reliability + so we always have data
      // available even if the user closes the page mid-recording.
      rec.start(1000);
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      setPhase('recording');
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      setError("Le format d'enregistrement n'est pas supporté sur cet appareil.");
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch {}
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const reset = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsed(0);
    setError('');
    setPhase('idle');
  };

  const submit = async () => {
    if (!audioBlob) return;
    setPhase('uploading');
    setError('');
    try {
      const b64 = await blobToBase64(audioBlob);
      await axios.post(`${API_URL}/api/recitations/assignments/${assignmentId}/upload`,
        { audio_base64: b64, mime_type: mimeType, duration_sec: elapsed },
        { headers: headers() });
      setPhase('done');
      onSubmitted?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur d'envoi.");
      setPhase('reviewing');
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60), ss = (s % 60).toString().padStart(2, '0');
    return `${m}:${ss}`;
  };

  if (phase === 'done') {
    return (
      <GlassCard className="text-center py-8" data-testid="recitation-recorder-done">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
        <h3 className="text-lg font-serif text-text-primary mb-1">Enregistrement envoyé 🌙</h3>
        <p className="text-sm text-text-secondary">Votre professeur sera notifié et l'écoutera dès que possible.</p>
        <Button onClick={reset} variant="outline" className="mt-4" data-testid="recitation-record-again-btn">
          <RefreshCcw className="w-4 h-4 mr-1" /> Enregistrer une autre version
        </Button>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-5" data-testid="recitation-recorder">
      {error && (
        <div className="mb-3 p-3 rounded-lg bg-danger/15 border border-danger/30 text-danger text-sm" data-testid="recitation-recorder-error">
          {error}
        </div>
      )}

      {phase === 'idle' && (
        <div className="text-center py-4">
          <p className="text-sm text-text-secondary mb-4">
            Appuie sur le micro et récite calmement. Tu pourras réécouter avant d'envoyer.
          </p>
          <button
            onClick={startRecording}
            className="w-24 h-24 mx-auto rounded-full bg-primary text-background
                       flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
            data-testid="recitation-start-btn"
            aria-label="Commencer l'enregistrement"
          >
            <Mic className="w-10 h-10" />
          </button>
          <p className="text-xs text-text-muted mt-3">Durée maximum : 5 minutes</p>
        </div>
      )}

      {phase === 'recording' && (
        <div className="text-center py-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-3 h-3 rounded-full bg-danger animate-pulse" />
            <span className="text-text-primary font-medium font-mono text-lg">{fmt(elapsed)}</span>
          </div>
          <button
            onClick={stopRecording}
            className="w-24 h-24 mx-auto rounded-full bg-danger text-white
                       flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
            data-testid="recitation-stop-btn"
            aria-label="Arrêter"
          >
            <Square className="w-10 h-10" fill="currentColor" />
          </button>
          <p className="text-xs text-text-muted mt-3">Récite calmement. Appuie sur le carré pour arrêter.</p>
        </div>
      )}

      {phase === 'reviewing' && audioUrl && (
        <div className="space-y-4 py-2">
          <p className="text-sm text-text-secondary text-center">
            Réécoute ta récitation avant de l'envoyer à ton professeur.
          </p>
          <audio src={audioUrl} controls className="w-full" data-testid="recitation-preview-audio" />
          <p className="text-xs text-text-muted text-center">Durée : {fmt(elapsed)}</p>
          <div className="flex gap-2">
            <Button onClick={reset} variant="outline" className="flex-1" data-testid="recitation-redo-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Recommencer
            </Button>
            <Button onClick={submit} className="flex-1 bg-primary text-background hover:bg-primary-hover"
                    data-testid="recitation-submit-btn">
              <Send className="w-4 h-4 mr-1" /> Envoyer
            </Button>
          </div>
        </div>
      )}

      {phase === 'uploading' && (
        <div className="text-center py-8">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-text-secondary">Envoi en cours…</p>
        </div>
      )}
    </GlassCard>
  );
};

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const dataUrl = r.result; // "data:audio/webm;base64,xxxx"
      const idx = dataUrl.indexOf(',');
      resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

export default AssignmentRecitationRecorder;
